import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  LinearProgress,
  Slider,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CalculateIcon from '@mui/icons-material/Calculate';
import FlagIcon from '@mui/icons-material/Flag';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import TuneIcon from '@mui/icons-material/Tune';
import {
  IObjectiveGapEvaluation,
  IObjectiveGapDriver,
  IObjectiveGapWorkbench,
  ObjectiveGapComputeMode,
} from './simulation.models';

type Props = {
  sourcePartialResult?: number;
  workbench?: IObjectiveGapWorkbench | null;
  lastEvaluation?: IObjectiveGapEvaluation | null;
  loading?: boolean;
  onStart: (targetvalue: number, mode: ObjectiveGapComputeMode) => Promise<void>;
  onModeChange: (mode: ObjectiveGapComputeMode) => Promise<void>;
  onEvaluate: (drivercode: string, value: number) => Promise<void>;
  onRefresh: () => Promise<void> | void;
};

type AutoStatus = 'IDLE' | 'SCHEDULED' | 'PROCESSING' | 'COMPLETE';

type State = {
  targetvalue: string;
  mode: ObjectiveGapComputeMode;
  values: Record<string, number>;
  dirty: Record<string, boolean>;
  error: string;
  autoStatus: AutoStatus;
};

const fmt = (value?: number | null, maximumFractionDigits = 2): string => {
  if (value === undefined || value === null || !Number.isFinite(Number(value))) return '—';
  return Number(value).toLocaleString(undefined, { maximumFractionDigits });
};

const money = (value?: number | null): string => `${fmt(value, 0)} FCFA`;

class SimulationObjectiveGapPanel extends React.Component<Props, State> {
  private autoTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly autoDebounceMs = 750;

  state: State = {
    targetvalue: this.props.workbench?.objective?.targetvalue?.toString() ?? '',
    mode: this.props.workbench?.session?.computemode ?? 'MANUAL',
    values: this.driverValues(this.props.workbench),
    dirty: {},
    error: '',
    autoStatus: 'IDLE',
  };

  componentDidUpdate(prev: Props) {
    if (prev.workbench !== this.props.workbench) {
      this.clearAutoTimer();
      this.setState({
        targetvalue: this.props.workbench?.objective?.targetvalue?.toString() ?? this.state.targetvalue,
        mode: this.props.workbench?.session?.computemode ?? this.state.mode,
        values: this.driverValues(this.props.workbench),
        dirty: {},
        error: '',
        autoStatus: prev.loading ? 'COMPLETE' : 'IDLE',
      });
    }
  }

  componentWillUnmount() {
    this.clearAutoTimer();
  }

  private driverValues(workbench?: IObjectiveGapWorkbench | null): Record<string, number> {
    const values: Record<string, number> = {};
    (workbench?.drivers || []).forEach((driver) => {
      values[driver.drivercode] = Number(driver.currentvalue ?? driver.baselinevalue ?? 0);
    });
    return values;
  }

  private clearAutoTimer = () => {
    if (this.autoTimer) {
      clearTimeout(this.autoTimer);
      this.autoTimer = null;
    }
  };

  private progressValue = (): number => {
    const value = Number(this.props.workbench?.objective?.achievementpercent ?? 0);
    if (!Number.isFinite(value)) return 0;
    return Math.max(0, Math.min(100, value));
  };

  private validateValue = (driver: IObjectiveGapDriver, value: number): string => {
    if (!Number.isFinite(value)) return 'Enter a valid numeric value.';
    if (driver.effectiveminvalue != null && value < Number(driver.effectiveminvalue)) {
      return `Minimum governed value is ${fmt(driver.effectiveminvalue)}.`;
    }
    if (driver.effectivemaxvalue != null && value > Number(driver.effectivemaxvalue)) {
      return `Maximum governed value is ${fmt(driver.effectivemaxvalue)}.`;
    }
    return '';
  };

  private setDriver = (driver: IObjectiveGapDriver, value: number) => {
    this.setState(
      (state) => ({
        values: { ...state.values, [driver.drivercode]: value },
        dirty: { ...state.dirty, [driver.drivercode]: true },
        error: '',
        autoStatus: 'IDLE',
      }),
      () => {
        if (this.state.mode === 'AUTO') this.scheduleAutoEvaluate(driver);
      },
    );
  };

  private scheduleAutoEvaluate = (driver: IObjectiveGapDriver) => {
    this.clearAutoTimer();
    const value = Number(this.state.values[driver.drivercode]);
    const error = this.validateValue(driver, value);
    if (error) {
      this.setState({ error, autoStatus: 'IDLE' });
      return;
    }

    this.setState({ autoStatus: 'SCHEDULED' });
    this.autoTimer = setTimeout(async () => {
      this.autoTimer = null;
      this.setState({ autoStatus: 'PROCESSING', error: '' });
      try {
        await this.props.onEvaluate(driver.drivercode, value);
        this.setState((state) => ({
          dirty: { ...state.dirty, [driver.drivercode]: false },
          autoStatus: 'COMPLETE',
        }));
      } catch (error: any) {
        this.setState({
          error: error?.message || 'Objective Gap automatic evaluation failed.',
          autoStatus: 'IDLE',
        });
      }
    }, this.autoDebounceMs);
  };

  private evaluateManual = async (driver: IObjectiveGapDriver) => {
    const value = Number(this.state.values[driver.drivercode]);
    const error = this.validateValue(driver, value);
    if (error) {
      this.setState({ error });
      return;
    }
    await this.props.onEvaluate(driver.drivercode, value);
    this.setState((state) => ({
      dirty: { ...state.dirty, [driver.drivercode]: false },
      error: '',
    }));
  };

  private start = async () => {
    const target = Number(this.state.targetvalue);
    if (!Number.isFinite(target)) {
      this.setState({ error: 'Enter a valid desired Partial Result.' });
      return;
    }
    await this.props.onStart(target, this.state.mode);
  };

  private changeMode = async (mode: ObjectiveGapComputeMode) => {
    this.clearAutoTimer();
    this.setState({ mode, autoStatus: 'IDLE', error: '' });
    if (this.props.workbench?.session?.sessioncode) {
      await this.props.onModeChange(mode);
    }
  };

  private renderSetup() {
    const active = Boolean(this.props.workbench?.session?.sessioncode);
    return (
      <Card elevation={1}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 3 }}>
              <Typography variant="overline" color="text.secondary">Current Partial Result</Typography>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                {money(this.props.workbench?.objective?.baselinevalue ?? this.props.sourcePartialResult)}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Desired Partial Result"
                value={this.state.targetvalue}
                disabled={this.props.loading}
                onChange={(event) => this.setState({ targetvalue: event.target.value, error: '' })}
                helperText="Management target for the Objective Gap analysis."
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Stack spacing={0.5}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                  Evaluation Mode
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={this.state.mode}
                  disabled={this.props.loading}
                  onChange={(_, mode: ObjectiveGapComputeMode | null) => {
                    if (mode) void this.changeMode(mode);
                  }}
                >
                  <Tooltip title="Evaluate automatically after a lever settles">
                    <ToggleButton value="AUTO"><AutoAwesomeIcon fontSize="small" sx={{ mr: 0.5 }} />Auto</ToggleButton>
                  </Tooltip>
                  <Tooltip title="Adjust first, then explicitly evaluate">
                    <ToggleButton value="MANUAL"><TuneIcon fontSize="small" sx={{ mr: 0.5 }} />Manual</ToggleButton>
                  </Tooltip>
                </ToggleButtonGroup>
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <Button
                fullWidth
                variant={active ? 'outlined' : 'contained'}
                startIcon={active ? <RestartAltIcon /> : <FlagIcon />}
                disabled={this.props.loading}
                onClick={() => void this.start()}
              >
                {active ? 'Restart' : 'Start'}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  }

  private renderProgress() {
    const w = this.props.workbench;
    if (!w) return null;
    const o = w.objective;
    const achieved = Number(o.achievementpercent ?? 0);
    const reached = Math.abs(Number(o.remaininggap ?? 0)) <= Number(o.tolerancevalue ?? 0);

    return (
      <Card elevation={1}>
        <CardContent>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} justifyContent="space-between">
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Partial Result Objective</Typography>
                <Typography variant="body2" color="text.secondary">
                  Close the gap using only governed management-controllable or influenceable levers.
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip size="small" label={w.session.statecode} variant="outlined" />
                <Chip size="small" label={w.session.computemode} color={w.session.computemode === 'AUTO' ? 'info' : 'default'} />
                <Chip size="small" label={reached ? 'TARGET REACHED' : `${fmt(achieved, 1)}% CLOSED`} color={reached ? 'success' : achieved < 0 ? 'warning' : 'info'} />
              </Stack>
            </Stack>

            <Grid container spacing={2}>
              <Grid size={{ xs: 6, md: 3 }}><Typography variant="caption" color="text.secondary">Baseline</Typography><Typography sx={{ fontWeight: 800 }}>{money(o.baselinevalue)}</Typography></Grid>
              <Grid size={{ xs: 6, md: 3 }}><Typography variant="caption" color="text.secondary">Target</Typography><Typography sx={{ fontWeight: 800 }}>{money(o.targetvalue)}</Typography></Grid>
              <Grid size={{ xs: 6, md: 3 }}><Typography variant="caption" color="text.secondary">Current Result</Typography><Typography sx={{ fontWeight: 800 }}>{money(o.currentvalue)}</Typography></Grid>
              <Grid size={{ xs: 6, md: 3 }}><Typography variant="caption" color="text.secondary">Remaining Gap</Typography><Typography sx={{ fontWeight: 800 }}>{money(o.remaininggap)}</Typography></Grid>
            </Grid>

            <LinearProgress variant="determinate" value={this.progressValue()} sx={{ height: 10, borderRadius: 5 }} />
          </Stack>
        </CardContent>
      </Card>
    );
  }

  private renderAutoStatus() {
    if (this.state.mode !== 'AUTO') return null;
    if (this.state.autoStatus === 'IDLE') return null;
    return (
      <Alert severity={this.state.autoStatus === 'COMPLETE' ? 'success' : 'info'} icon={this.state.autoStatus === 'PROCESSING' ? <CircularProgress size={18} /> : undefined}>
        {this.state.autoStatus === 'SCHEDULED' && 'AUTO: waiting for the lever adjustment to settle…'}
        {this.state.autoStatus === 'PROCESSING' && 'AUTO: evaluating the management lever against the Partial Result target…'}
        {this.state.autoStatus === 'COMPLETE' && 'AUTO: Objective Gap impact refreshed.'}
      </Alert>
    );
  }

  private renderLastImpact() {
    const e = this.props.lastEvaluation;
    if (!e?.impact) return null;
    const positive = Number(e.impact.impactvalue ?? 0) > 0;
    return (
      <Alert severity={positive ? 'success' : Number(e.impact.impactvalue ?? 0) < 0 ? 'warning' : 'info'}>
        <strong>{e.adjustment?.drivercode}</strong>: Partial Result impact {money(e.impact.impactvalue)}; remaining gap {money(e.impact.remaininggap)}; achievement {fmt(e.impact.achievementpercent, 2)}%.
      </Alert>
    );
  }

  private renderDriver(driver: IObjectiveGapDriver) {
    const value = Number(this.state.values[driver.drivercode] ?? driver.currentvalue ?? driver.baselinevalue ?? 0);
    const min = driver.effectiveminvalue == null ? undefined : Number(driver.effectiveminvalue);
    const max = driver.effectivemaxvalue == null ? undefined : Number(driver.effectivemaxvalue);
    const hasSlider = min != null && max != null && Number.isFinite(min) && Number.isFinite(max) && max > min;
    const dirty = Boolean(this.state.dirty[driver.drivercode]);
    const error = this.validateValue(driver, value);

    return (
      <Grid size={{ xs: 12, md: 6 }} key={driver.drivercode}>
        <Card elevation={1} sx={{ height: '100%' }}>
          <CardContent>
            <Stack spacing={1.5}>
              <Stack direction="row" justifyContent="space-between" spacing={1}>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{driver.drivername}</Typography>
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                    <Chip size="small" label={driver.drivercode} variant="outlined" />
                    <Chip size="small" label={driver.controlclass} color={driver.controlclass === 'CONTROLLABLE' ? 'success' : 'info'} variant="outlined" />
                    <Chip size="small" label={driver.directioncode} variant="outlined" />
                    <Chip size="small" label={driver.adjustmentbasis} variant="outlined" />
                  </Stack>
                </Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{fmt(value)} {driver.unit || ''}</Typography>
              </Stack>

              <Grid container spacing={1}>
                <Grid size={{ xs: 6 }}><Typography variant="caption" color="text.secondary">Source baseline</Typography><Typography variant="body2" sx={{ fontWeight: 700 }}>{fmt(driver.baselinevalue)} {driver.unit || ''}</Typography></Grid>
                <Grid size={{ xs: 6 }}><Typography variant="caption" color="text.secondary">Current analytical</Typography><Typography variant="body2" sx={{ fontWeight: 700 }}>{fmt(driver.currentvalue)} {driver.unit || ''}</Typography></Grid>
              </Grid>

              <TextField
                size="small"
                fullWidth
                type="number"
                label="Objective Gap Lever Value"
                value={value}
                disabled={this.props.loading}
                error={Boolean(error)}
                helperText={error || `Governed range: ${fmt(min)} to ${fmt(max)} ${driver.unit || ''}`}
                onChange={(event) => this.setDriver(driver, Number(event.target.value))}
              />

              {hasSlider && (
                <>
                  <Slider
                    value={Math.max(min!, Math.min(max!, value))}
                    min={min}
                    max={max}
                    step={driver.adjustmentbasis === 'ABSOLUTE' && driver.unit === 'PCT' ? 0.5 : Math.max((max! - min!) / 100, 0.000001)}
                    disabled={this.props.loading}
                    valueLabelDisplay="auto"
                    onChange={(_, next) => this.setDriver(driver, Number(next))}
                  />
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="caption">{fmt(min)} {driver.unit || ''}</Typography>
                    <Typography variant="caption" color="text.secondary">Governed executable envelope</Typography>
                    <Typography variant="caption">{fmt(max)} {driver.unit || ''}</Typography>
                  </Stack>
                </>
              )}

              {driver.requiresconstraint && (
                <Typography variant="caption" color="warning.main">This lever is subject to additional management/business constraints.</Typography>
              )}

              {this.state.mode === 'MANUAL' && (
                <Button
                  variant="contained"
                  startIcon={<CalculateIcon />}
                  disabled={!dirty || Boolean(error) || this.props.loading}
                  onClick={() => void this.evaluateManual(driver)}
                  sx={{ alignSelf: 'flex-end' }}
                >
                  Evaluate Impact
                </Button>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Grid>
    );
  }

  render() {
    const w = this.props.workbench;
    return (
      <Stack spacing={2}>
        {this.state.error && <Alert severity="warning">{this.state.error}</Alert>}
        {this.renderSetup()}

        {!w && (
          <Alert severity="info">
            Set the desired Partial Result and start an Objective Gap session. The analytical workspace will be isolated from the authoritative Simulation scenario.
          </Alert>
        )}

        {w && (
          <>
            {this.renderProgress()}
            {this.renderAutoStatus()}
            {this.renderLastImpact()}

            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>Governed Management Levers</Typography>
                <Typography variant="body2" color="text.secondary">
                  Only drivers approved by Objective Gap policy are shown. Exogenous, calculated, legacy and policy-pending drivers are excluded.
                </Typography>
              </Box>
              <Button size="small" variant="outlined" onClick={() => void this.props.onRefresh()} disabled={this.props.loading}>Refresh</Button>
            </Stack>

            <Grid container spacing={2}>
              {(w.drivers || []).map((driver) => this.renderDriver(driver))}
            </Grid>
          </>
        )}
      </Stack>
    );
  }
}

export default SimulationObjectiveGapPanel;
